import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, ModeBadge, ScopeChips } from "@/pages/Dashboard";
import { Buildings, MagnifyingGlass } from "@phosphor-icons/react";

export default function SubMerchants() {
  const [data, setData] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/submerchants").then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = data.filter(s => {
    const t = q.toLowerCase();
    return !t || s.business_name.toLowerCase().includes(t) || s.razorpay_account_id.toLowerCase().includes(t) || s.email.toLowerCase().includes(t);
  });

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Step 3 · Connections</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Sub-merchants</h1>
        <p className="mt-1.5 text-sm text-slate-500">Razorpay accounts that have authorized your applications.</p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or account_id" data-testid="submerchants-search"
            className="w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15" />
        </div>
        <div className="text-xs text-slate-500" data-testid="submerchants-count">{filtered.length} connected</div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <Empty />
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white">
          <table className="w-full text-sm" data-testid="submerchants-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <Th>Sub-merchant</Th>
                <Th>Razorpay account</Th>
                <Th>Scopes</Th>
                <Th>Mode</Th>
                <Th>Status</Th>
                <Th>Connected</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {filtered.map(sm => (
                <tr key={sm.id} className="hover:bg-orange-50/40 transition-colors">
                  <td className="px-4 py-3">
                    <Link to={`/submerchants/${sm.id}`} className="font-medium text-[#0F172A] hover:text-[#FF6B00]" data-testid={`sm-row-${sm.id}`}>
                      {sm.business_name}
                    </Link>
                    <div className="text-xs text-slate-500">{sm.email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{sm.razorpay_account_id}</td>
                  <td className="px-4 py-3"><ScopeChips scopes={sm.scopes} /></td>
                  <td className="px-4 py-3"><ModeBadge mode={sm.mode} /></td>
                  <td className="px-4 py-3"><StatusBadge status={sm.status} /></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{new Date(sm.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-left font-semibold">{children}</th>; }

function Empty() {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="empty-submerchants">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF3E0]">
        <Buildings size={24} className="text-[#FF6B00]" weight="duotone" />
      </div>
      <h3 className="font-display text-lg font-semibold">No sub-merchants connected yet</h3>
      <p className="mt-1.5 mx-auto max-w-md text-sm text-slate-500">
        Build your authorize URL, share it with a sub-merchant, and they'll appear here once they approve.
      </p>
      <Link to="/authorize-builder" className="btn-primary mt-5 inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium">
        Open Authorize URL Builder
      </Link>
    </div>
  );
}
